import { useGetIdentity, useGetList, useUpdateMany } from "ra-core";
import { Bell } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Notification } from "../types";

/**
 * In-app notifications (decision #53) - a workflow's "notify a teammate"
 * action lands here (and, separately, as an email through the system
 * alert rail once Postmark is configured). Polls rather than subscribing
 * live: simple, and a 30s staleness on a notification bell is unnoticeable.
 */
export const NotificationBell = () => {
  const { identity } = useGetIdentity();
  const { data: notifications = [] } = useGetList<Notification>(
    "notifications",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "created_at", order: "DESC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: !!identity, refetchInterval: 30_000 },
  );
  const [updateMany] = useUpdateMany();

  const unread = notifications.filter((n) => !n.read_at);

  const markRead = (ids: Notification["id"][]) => {
    if (ids.length === 0) return;
    updateMany("notifications", {
      ids,
      data: { read_at: new Date().toISOString() },
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative inline-flex items-center justify-center w-8 h-8 rounded-md text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-secondary-foreground/10 transition-colors"
        >
          <Bell className="w-4 h-4" />
          {unread.length > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none"
            >
              {unread.length > 9 ? "9+" : unread.length}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 flex flex-col gap-2 p-3" align="end">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Notifications</p>
          {unread.length > 0 && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => markRead(unread.map((n) => n.id))}
            >
              Mark all as read
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            You're all caught up.
          </p>
        ) : (
          <div className="flex flex-col gap-1 max-h-96 overflow-y-auto -mx-1">
            {notifications.map((n) => (
              <Link
                key={n.id}
                to={n.link ?? "#"}
                onClick={() => markRead(n.read_at ? [] : [n.id])}
                className={`flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary-foreground/5 transition-colors ${
                  n.read_at ? "" : "bg-primary/5"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {!n.read_at && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  )}
                  <span className="font-medium truncate">{n.title}</span>
                </span>
                {n.body && (
                  <span className="text-xs text-muted-foreground line-clamp-2">
                    {n.body}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
